import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "mental-health-web-design-colorado",
  metaTitle: "Mental Health Practice Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom mental health web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Mental Health Practice Web Design Colorado",
  eyebrow: "Mental Health Practice Web Design — Colorado",
  h1: (
    <>
      Mental Health Practice <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded mental health websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Mental Health websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first mental health sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Mental Health Practice web design in Colorado",
    body: "Mental Health Practice web design is the practice of building conversion-focused, mobile-first websites for mental health businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with confidential intake above the fold." },
    { title: "Issue-based service pages", body: "Anxiety, depression, trauma, couples, teen, EMDR, medication — each ranks independently." },
    { title: "HIPAA-aware intake", body: "Forms scoped to non-PHI fields with secure handoff to your EHR or scheduling." },
    { title: "Therapist bio pages", body: "Each clinician with modalities, populations served, and direct booking." },
    { title: "Insurance & self-pay clarity", body: "Accepted plans and self-pay sliding scale surfaced where patients look." },
    { title: "Schema and SEO at launch", body: "MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Patients search by issue, not by \"therapist\"", body: "We build pages for \"anxiety therapy\", \"trauma therapy\", \"couples counseling\" — actual queries." },
    { heading: "Therapist fit drives the booking", body: "Bio pages with modalities, photos, and populations served close the intake." },
    { heading: "Insurance clarity reduces churn", body: "Above-the-fold insurance + self-pay clarity cuts front-desk volume and intake friction." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a mental health website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a mental health website?", a: "Typical timeline is 4–6 weeks for a 12–20 page mental health site with full service and service-area pages. Larger multi-location mental health businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs mental health businesses?", a: "No — we build mental health sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Mental Health Practice Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for mental health businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/mental-health-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
